// src/utils/api.js
//
// Shared helpers for the artifact-signing wizard.
//   * every request carries the login token (localStorage "token")
//   * requests time out instead of hanging
//   * malformed / non-JSON responses never crash a page
//   * downloads are authenticated and use the file name chosen by the server
import { API_URL } from "../config";

export const getToken = () => localStorage.getItem("token") || "";

export const authHeaders = (extra = {}) => {
  const token = getToken();
  return token ? { ...extra, Authorization: `Bearer ${token}` } : { ...extra };
};

export class ApiError extends Error {
  constructor(message, status = 0, data = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

const SESSION_MESSAGE =
  "You are not signed in, or your session has expired. Please sign in again.";

const messageFrom = (data, fallback) =>
  (data && (data.message || data.error)) || fallback;

// apiRequest("/api/artifact/sign", { method: "POST", json: {...} })
// apiRequest("/api/artifact/upload", { method: "POST", formData })
export async function apiRequest(
  path,
  { method = "GET", json, formData, headers = {}, timeoutMs = 120000 } = {}
) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const init = { method, headers: authHeaders(headers), signal: controller.signal };
  if (json !== undefined) {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(json);
  } else if (formData) {
    init.body = formData; // the browser adds the multipart boundary itself
  }

  let res;
  try {
    res = await fetch(`${API_URL}${path}`, init);
  } catch (err) {
    throw new ApiError(
      err && err.name === "AbortError"
        ? "The request timed out. Please try again."
        : "Cannot reach the server. Check your connection and try again."
    );
  } finally {
    clearTimeout(timer);
  }

  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (res.status === 401) throw new ApiError(SESSION_MESSAGE, 401, data);
  if (!res.ok) {
    throw new ApiError(messageFrom(data, `Request failed (${res.status}).`), res.status, data);
  }
  return data;
}

const filenameFromDisposition = (header) => {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : null;
};

// Authenticated download. Returns the file name that was saved.
export async function downloadWithAuth(path, fallbackName = "download") {
  let res;
  try {
    res = await fetch(`${API_URL}${path}`, { headers: authHeaders() });
  } catch {
    throw new ApiError("Cannot reach the server. Check your connection and try again.");
  }

  if (res.status === 401) throw new ApiError(SESSION_MESSAGE, 401);
  if (!res.ok) {
    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }
    throw new ApiError(messageFrom(data, "Download failed."), res.status, data);
  }

  const blob = await res.blob();
  const name = filenameFromDisposition(res.headers.get("Content-Disposition")) || fallbackName;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return name;
}

export const WIZARD_STEPS = [
  "Choose Method",
  "Upload",
  "Generate Manifest",
  "Choose Digital ID",
  "Review & Sign",
  "Verify & Complete",
];

// What a Digital ID's identity_type means for the person choosing it.
export const PROFILE_LABELS = {
  pq: "Post-quantum",
  hybrid: "Hybrid (PQ + ECDSA P-256)",
  classical: "Classical (ECDSA P-256)",
};

export const profileLabel = (identityType) =>
  PROFILE_LABELS[String(identityType || "").toLowerCase()] || "Unknown profile";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Returns the reason a Digital ID cannot sign, or null when it is eligible.
// This only improves the screens; the backend repeats every check.
export function eligibilityProblem(item) {
  if (!item) return "No Digital ID selected.";
  const status = String(item.status || "").toUpperCase();
  if (status !== "ACTIVE") return `This Digital ID is ${status ? status.toLowerCase() : "not active"}.`;
  const today = todayISO();
  if (item.valid_from && today < item.valid_from) return "Not valid yet.";
  if (item.valid_till && today > item.valid_till) return "Expired. Renew it before signing.";
  if (String(item.identity_type || "").toLowerCase() === "classical" && /RSA/i.test(item.algorithm || "")) {
    return "RSA Digital IDs cannot sign artifacts.";
  }
  return null;
}
